/*
  © 2023 KondaSoft
  https://www.kondasoft.com
*/

/**
 * @typedef {import("swiper/types").Swiper} Swiper
 * @typedef {import("swiper/types").SwiperOptions} SwiperOptions
*/

class ProductCardImageGallery extends HTMLElement {
  constructor () {
    super()

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper'), this.splideOptions())
  }

  splideOptions () {
    /** @type {SwiperOptions} */
    const options = {
      rewind: true,
      speed: 300,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      }
    }
    return options
  }
}
customElements.define('product-card-image-gallery', ProductCardImageGallery)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect,
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-template') && window.matchMedia('(min-width: 992px)').matches) {
      const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
      this.style.top = `${navbarHeight + 20}px`
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (!this.dataset.vendorGlightboxJs) return

    const vendorStyle = document.createElement('link')
    vendorStyle.rel = 'stylesheet'
    vendorStyle.href = this.dataset.vendorGlightboxCss
    document.head.appendChild(vendorStyle)

    const vendorScript = document.createElement('script')
    vendorScript.src = this.dataset.vendorGlightboxJs
    document.head.appendChild(vendorScript)

    await new Promise(resolve => setTimeout(resolve, 200))

    window.GLightbox({
      selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
      loop: true,
      openEffect: 'zoom',
      closeEffect: 'fade',
      slideEffect: this.dataset.fullScreenEffect
    })
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)
